import * as React from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Bell, Building2, FileText, KanbanSquare, LogOut, Receipt } from "lucide-react";
import {
  AppShell,
  Boton,
  Pill,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Sheet,
  SheetContenido,
  SheetDescripcion,
  SheetDisparador,
  SheetTitulo,
  Vacio,
  type EntradaNav,
} from "@iavmx/ui";
import { leer } from "../api";
import { useEquipo, useOdoo, useYo } from "../datos";
import { relativo } from "../formato";
import { useSesion } from "../sesion";
import type { Aviso } from "../tipos";

function EstadoOdoo() {
  const q = useOdoo();
  if (!q.data) return null;
  const o = q.data;
  const texto = o.conectado
    ? o.ultimaSincronizacionEn
      ? `Odoo sincronizado ${relativo(o.ultimaSincronizacionEn)}`
      : "Odoo conectado · sin sincronizar aún"
    : "Odoo sin conectar";
  return (
    <span className="hidden items-center gap-1.5 font-ui text-xs text-tinta-tenue lg:inline-flex" title={o.motivo ?? `Transporte ${o.transporte}`}>
      <span className={o.conectado ? "size-2 rounded-full bg-verde" : "size-2 rounded-full bg-tinta-tenue"} aria-hidden />
      {texto}
    </span>
  );
}

function Bandeja() {
  const { api, listo } = useSesion();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const q = useQuery({
    queryKey: ["bandeja"],
    queryFn: () => api.api.bandeja.$get().then((r) => leer<{ avisos: Aviso[]; noLeidas: number }>(r)),
    enabled: listo,
    refetchInterval: 60_000,
  });
  const leerAviso = useMutation({
    mutationFn: (id: string) => api.api.bandeja[":id"].leida.$post({ param: { id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["bandeja"] }),
  });
  const n = q.data?.noLeidas ?? 0;
  return (
    <Sheet>
      <SheetDisparador asChild>
        <Boton variante="fantasma" tamano="icono" aria-label={n ? `Avisos, ${n} sin leer` : "Avisos"} className="relative">
          <Bell />
          {n > 0 && <span className="absolute right-1 top-1 min-w-4 rounded-full bg-tinta px-1 text-center font-ui text-[10px] font-semibold leading-4 text-tinta-inversa">{n}</span>}
        </Boton>
      </SheetDisparador>
      <SheetContenido className="w-96 max-w-[90vw] overflow-y-auto">
        <SheetTitulo>Avisos</SheetTitulo>
        <SheetDescripcion>Aperturas, aceptaciones, descuentos por firmar y oportunidades detenidas.</SheetDescripcion>
        <ul className="mt-4 divide-y divide-linea">
          {(q.data?.avisos ?? []).map((a) => (
            <li key={a.id} className="py-3">
              <button
                type="button"
                className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => {
                  if (!a.leidaEn) leerAviso.mutate(a.id);
                  if (a.url) {
                    const u = a.url.startsWith("http") ? new URL(a.url).pathname : a.url;
                    void navegar({ to: u as never });
                  }
                }}
              >
                <div className="flex items-start gap-2">
                  {!a.leidaEn && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-naranja" aria-label="Sin leer" />}
                  <div className="min-w-0">
                    <div className="font-ui text-sm font-semibold">{a.titulo}</div>
                    {a.texto && <div className="font-ui text-xs text-tinta-3">{a.texto}</div>}
                    <div className="mt-0.5 font-ui text-xs text-tinta-tenue">{relativo(a.creadoEn)}</div>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
        {q.data && q.data.avisos.length === 0 && <p className="mt-4 font-ui text-sm text-tinta-tenue">Sin avisos todavía.</p>}
      </SheetContenido>
    </Sheet>
  );
}

/** Solo local: elige con qué persona del equipo actúa la API. */
function ActuarComo({ className }: { className?: string }) {
  const { dev, correo, actuandoComo, actuarComo } = useSesion();
  const equipo = useEquipo();
  const qc = useQueryClient();
  if (!dev || correo) return null;
  return (
    <Select
      value={actuandoComo ?? "__dev__"}
      onValueChange={(v) => {
        actuarComo(v === "__dev__" ? null : v);
        void qc.invalidateQueries();
      }}
    >
      <SelectDisparador className={`h-8 w-auto max-w-56 gap-1 text-xs ${className ?? ""}`} aria-label="Actuar como (solo desarrollo)">
        <SelectValor placeholder="Actuar como" />
      </SelectDisparador>
      <SelectContenido>
        <SelectOpcion value="__dev__">Bianca Aray · DEV_PERSONA</SelectOpcion>
        {(equipo.data?.equipo ?? [])
          .filter((p) => p.correo !== "cramirez@vanguardia.com.mx")
          .map((p) => (
            <SelectOpcion key={p.id} value={p.correo}>
              {p.nombre} · {p.puesto ?? p.rol}
            </SelectOpcion>
          ))}
      </SelectContenido>
    </Select>
  );
}

export function Raiz() {
  const ruta = useRouterState({ select: (s) => s.location.pathname });
  const { correo, cerrar, entrar, configuracion, listo, cargando, dev } = useSesion();
  const yo = useYo();
  const entradas: { etiqueta: string; to: "/" | "/cuentas" | "/cotizaciones" | "/propuestas" | "/tablero"; icono: React.ReactNode }[] = [
    { etiqueta: "Pipeline", to: "/", icono: <KanbanSquare /> },
    { etiqueta: "Cuentas", to: "/cuentas", icono: <Building2 /> },
    { etiqueta: "Cotizaciones", to: "/cotizaciones", icono: <Receipt /> },
    { etiqueta: "Propuestas", to: "/propuestas", icono: <FileText /> },
    { etiqueta: "Tablero", to: "/tablero", icono: <BarChart3 /> },
  ];
  const nav: EntradaNav[] = entradas.map((e) => ({
    etiqueta: e.etiqueta,
    href: e.to,
    icono: e.icono,
    activo: e.to === "/" ? ruta === "/" || ruta.startsWith("/oportunidades") : ruta.startsWith(e.to),
    render: ({ className, children }) => (
      <Link to={e.to} className={className} aria-current={ruta === e.to ? "page" : undefined}>
        {children}
      </Link>
    ),
  }));
  const persona = yo.data
    ? { nombre: yo.data.nombre, correo: yo.data.correo }
    : correo
      ? { nombre: correo.split("@")[0]!.replace(/[._]/g, " "), correo }
      : null;
  return (
    <AppShell
      producto="Comercial"
      nav={nav}
      persona={persona}
      otrasApps={[{ etiqueta: "Portal", href: configuracion?.urlPortal ?? "https://iavmx.com" }]}
      acciones={
        <>
          <EstadoOdoo />
          {listo && <ActuarComo className="hidden sm:flex" />}
          {listo && <Bandeja />}
          {correo ? (
            <Boton variante="fantasma" tamano="sm" onClick={() => void cerrar()}>
              <LogOut /> Salir
            </Boton>
          ) : !dev && !cargando ? (
            <Boton variante="acento" tamano="sm" onClick={() => void entrar()}>
              Entrar
            </Boton>
          ) : null}
        </>
      }
    >
      {dev && !correo && (
        <div className="mb-4 flex flex-wrap items-center gap-2 font-ui text-xs text-tinta-tenue">
          <Pill tono="azul" className="whitespace-nowrap">Modo de desarrollo</Pill>
          <span>Actúas como {yo.data?.nombre ?? "la persona de DEV_PERSONA"} sin sesión de Google; solo en local.</span>
          {listo && <ActuarComo className="sm:hidden" />}
        </div>
      )}
      {!listo && !cargando ? (
        <Vacio titulo="Entra con tu cuenta de Google del trabajo" texto="Comercial es de Comercial: solo el equipo y la gerencia entran." accion={<Boton variante="acento" onClick={() => void entrar()}>Entrar con Google</Boton>} />
      ) : (
        <Outlet />
      )}
    </AppShell>
  );
}
